import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  RadioGroup,
  Button,
  Typography,
  Divider,
  Grid,
  Paper,
  Box,
  Tooltip,
  Chip,
  Alert,
} from "@mui/material";
import { PLAN_DETAILS } from "../../utils/common";

// Importing icons
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export const PlanExtensionDialog = ({ open, onClose, onBuy, cat }) => {
  const [selectedOption, setSelectedOption] = useState("1_day");

  const selectedPlan = PLAN_DETAILS[selectedOption];

  const handleBuy = () => {
    onBuy(selectedOption);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 4,
        sx: {
          borderRadius: 3,
          backgroundColor: "#fff",
          px: 2,
          py: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            color: "text.primary",
            letterSpacing: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          🚀 Extend Your Limit for
          <span
            style={{
              backgroundColor: "#cce3de",
              borderRadius: "5px",
              padding: "0 5px",
            }}
          >
            {cat + " Reportings"}
          </span>
        </Typography>
        <Tooltip title="Close" placement="left">
          <CancelIcon
            onClick={onClose}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              borderColor: "#FF5722",
              color: "#FF5722",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#fff5f2",
                borderColor: "#FF5722",
              },
            }}
          />
        </Tooltip>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Left Side – Plan Selection */}
          <Grid item xs={12} md={5}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a plan to extend your daily upload limit:
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                sx={{ gap: 2 }}
              >
                {Object.entries(PLAN_DETAILS).map(([key, plan]) => {
                  const isSelected = selectedOption === key;

                  return (
                    <Box
                      key={key}
                      sx={{
                        position: "relative",
                        p: 2,
                        borderRadius: 2,
                        border: `2px solid ${
                          isSelected ? plan.color : "#e0e0e0"
                        }`,
                        backgroundColor: isSelected
                          ? `${plan.color}15`
                          : "#fafafa",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? `${plan.color}22`
                            : "#f1f1f1",
                        },
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                      onClick={() => setSelectedOption(key)}
                    >
                      <input
                        type="radio"
                        id={key}
                        name="plan"
                        value={key}
                        checked={isSelected}
                        onChange={() => {}}
                        style={{ display: "none" }}
                      />
                      {isSelected && (
                        <CheckCircleIcon
                          fontSize="small"
                          sx={{ color: plan.color }}
                        />
                      )}
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          color={plan.color}
                          sx={{ mb: 0.5 }}
                        >
                          {plan.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plan.price}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                borderLeft: `5px solid ${selectedPlan.color}`,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    gutterBottom
                    color={selectedPlan.color}
                  >
                    {selectedPlan.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontSize: "15px", lineHeight: "22px" }}
                  >
                    {selectedPlan.description}
                  </Typography>
                </Box>

                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{
                    color: selectedPlan.color,
                    textShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    fontFamily: "'Roboto Slab', sans-serif",
                    mt: 2,
                  }}
                >
                  {selectedPlan.price}
                </Typography>
              </Box>

              <Button
                onClick={handleBuy}
                variant="contained"
                startIcon={<ShoppingCartIcon />}
                sx={{
                  backgroundColor: selectedPlan.color,
                  "&:hover": {
                    backgroundColor: selectedPlan.color,
                    opacity: 0.9,
                  },
                  fontWeight: 600,
                  alignSelf: "flex-start",
                  mt: 2,
                  width: "100%",
                  height: "50px",
                }}
              >
                Buy Now
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <Alert severity="info">
        Note: These plans apply <strong>only to {cat} Reportings</strong>. They
        are <strong>not applicable</strong> for other reporting categories.
      </Alert>
    </Dialog>
  );
};
